import { useClubHostReview } from "@/hooks/useClubHostReview";
import { clubHostTaskFingerprint } from "@/lib/clubHostReview";
import { useMemo, useState } from "react";
import { CLUB_ELO_ENABLED, SMART_NOTIFICATION_REVIEW_ENABLED } from "@/lib/rolloutGates";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useDirectConnectedInvitations } from "@/hooks/useDirectConnectedInvitations";
import {
  useConnectedReportNotifications,
  useMarkConnectedReportNotificationRead,
} from "@/hooks/useConnectedReportNotifications";
import { isAndroidInstalledApp, readPlayReviewState, recordPlayReviewClick, recordPlayReviewOptOut } from "@/lib/playReviewPrompt";
import {
  isSuggestionUnread,
  shouldSuggestEloVisibility,
  shouldSuggestPlayReview,
  shouldSuggestPrimaryClub,
  type NotificationSuggestionKey,
} from "@/lib/notificationRules";
import type { RankingProfile } from "@/lib/rankings";
import { isProfileComplete, isSnoozed, type ProfileCoreFields } from "@/lib/profileSetup";
import {
  matchesNeedingClubReview, possibleDuplicateOpponents, possibleDuplicateOwnClubs, reviewFingerprint,
  type OpponentIdentity, type MatchForClubReview, type MyClubIdentity,
} from "@/lib/identityReviewSuggestions";

export type CenterItem = {
  key: string;
  kind: "invitation" | "report" | "primary_club" | "elo_visibility" | "play_review"
    | "profile_completion" | "opponent_duplicates" | "club_review" | "club_duplicates";
  group: "action" | "update";
  titleKey: string;
  bodyKey: string;
  values?: Record<string, string>;
  href: string;
  unread: boolean;
  dismissible: boolean;
  sharedMatchId?: string;
};

type StateRow = {
  notification_key: NotificationSuggestionKey;
  read_at: string | null;
  dismissed_at: string | null;
};

const db = supabase as any;
const SUGGESTION_PREFIX = "suggestion:";

/**
 * Compose existing privacy-gated events with local, actionable suggestions.
 * No duplicated invitation or report rows: source RPCs own their lifecycle.
 * No inference of missing fields when a read fails.
 */
export function useNotificationCenter() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const id = user?.id;
  const client = useQueryClient();
  const invitations = useDirectConnectedInvitations();
  const reports = useConnectedReportNotifications();
  const matches = useQuery({
    queryKey: ["notification_center_match_count", id],
    enabled: !!id,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase.from("matches")
        .select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? 0;
    },
  });
  const markReportRead = useMarkConnectedReportNotificationRead();
  const [reviewClicked, setReviewClicked] = useState(false);
  const matchCount = matches.isSuccess ? (matches.data ?? 0) : 0;
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const shouldLoadProfile = !!id && matches.isSuccess && matchCount > 0;

  // All three checks read ONLY the signed-in user's private data.
  // No cross-user profile is accessed, and no merging is performed.
  const completion = useQuery({
    queryKey: ["notification_profile_completion", id],
    enabled: shouldLoadProfile && SMART_NOTIFICATION_REVIEW_ENABLED,
    retry: false,
    refetchInterval: 120_000,
    queryFn: async (): Promise<ProfileCoreFields | null> => {
      const { data, error } = await supabase.from("profiles")
        .select("display_name,date_of_birth,country,gender,level_rating")
        .eq("user_id", id!).maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
  const identityCandidates = useQuery({
    queryKey: ["notification_opponent_duplicates", id],
    enabled: shouldLoadProfile && SMART_NOTIFICATION_REVIEW_ENABLED,
    retry: false,
    staleTime: 120_000,
    refetchInterval: 300_000,
    queryFn: async (): Promise<OpponentIdentity[]> => {
      const { data, error } = await db.from("opponents")
        .select("id,name,linked_user_id")
        .eq("user_id", id!);
      if (error) throw error;
      return data ?? [];
    },
  });
  const hostClubTasks = useClubHostReview();
  const historicalClubs = useQuery({
    queryKey: ["notification_historical_clubs", id],
    enabled: shouldLoadProfile && SMART_NOTIFICATION_REVIEW_ENABLED,
    retry: false,
    staleTime: 120_000,
    refetchInterval: 300_000,
    queryFn: async (): Promise<MatchForClubReview[]> => {
      const { data, error } = await supabase.from("matches")
        .select("id,court,club_id,shared_match_id")
        .eq("user_id", id!)
        .is("club_id", null);
      if (error) throw error;
      return data ?? [];
    },
  });

  const states = useQuery({
    queryKey: ["notification_center_states", id],
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<StateRow[]> => {
      const { data, error } = await db.from("notification_center_states")
        .select("notification_key,read_at,dismissed_at")
        .eq("user_id", id);
      if (error) {
        // Migration-safe in the preview between frontend deployment and SQL rollout.
        if (error.code === "42P01" || error.code === "PGRST205" ||
            /could not find the table|does not exist/i.test(error.message || "")) return [];
        throw error;
      }
      return (data ?? []) as StateRow[];
    },
  });

  const clubs = useQuery({
    queryKey: ["notification_center_clubs", id],
    enabled: shouldLoadProfile && SMART_NOTIFICATION_REVIEW_ENABLED,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async (): Promise<MyClubIdentity[]> => {
      const { data, error } = await db.rpc("get_my_tennis_clubs");
      if (error) throw error;
      return (data ?? []) as MyClubIdentity[];
    },
  });

  const ranking = useQuery({
    queryKey: ["notification_center_ranking", id],
    enabled: shouldLoadProfile && SMART_NOTIFICATION_REVIEW_ENABLED,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async (): Promise<RankingProfile | null> => {
      const { data, error } = await db.rpc("get_my_tennis_ranking_profile");
      if (error) throw error;
      return (Array.isArray(data) ? data[0] : data) ?? null;
    },
  });

  const changeState = useMutation({
    mutationFn: async ({ key, dismiss }: { key: NotificationSuggestionKey; dismiss: boolean }) => {
      if (!id || !key.startsWith(SUGGESTION_PREFIX)) throw new Error("Not signed in");
      const now = new Date().toISOString();
      const { error } = await db.from("notification_center_states").upsert({
        user_id: id, notification_key: key,
        read_at: now, dismissed_at: dismiss ? now : null, updated_at: now,
      }, { onConflict: "user_id,notification_key" });
      if (error) throw error;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["notification_center_states", id] });
    },
  });

  const items = useMemo((): CenterItem[] => {
    if (!id) return [];
    const visible: CenterItem[] = [];
    for (const invitation of invitations.data ?? []) {
      const date = new Date(`${invitation.match_date}T12:00:00`).toLocaleDateString(locale);
      visible.push({
        key: `invitation:${invitation.invitation_id}`,
        kind: "invitation", group: "action",
        titleKey: "notificationCenter.inviteTitle", bodyKey: "notificationCenter.inviteBody",
        values: { name: invitation.inviter_display_name, date },
        href: `/app/invitations/${invitation.invitation_id}`,
        unread: true, dismissible: false,
      });
    }
    for (const report of reports.data ?? []) {
      visible.push({
        key: `report:${report.shared_match_id}`,
        kind: "report", group: "update",
        titleKey: "notificationCenter.reportTitle", bodyKey: "notificationCenter.reportBody",
        values: { name: report.opponent_name },
        href: `/app/edit/${report.current_match_id}#connected-performance-report`,
        unread: true, dismissible: false,
        sharedMatchId: report.shared_match_id,
      });
    }

    // A failed state query cannot be treated as permission to ignore opt-outs.
    // Keep event notifications usable but suppress suggestions until it succeeds.
    if (states.isError || !states.isSuccess) return visible;

    const byKey = new Map((states.data ?? []).map((row) => [row.notification_key, row]));
    const suggest = (
      key: NotificationSuggestionKey,
      kind: CenterItem["kind"],
      href: string,
    ) => {
      const state = byKey.get(key);
      if (state?.dismissed_at) return;
      visible.push({
        key, kind, group: "action",
        titleKey: `notificationCenter.${kind === "primary_club" ? "clubTitle" :
          kind === "elo_visibility" ? "rankingTitle" :
      kind === "profile_completion" ? "profileTitle" :
      kind === "opponent_duplicates" ? "duplicatesTitle" :
      kind === "club_review" ? "clubsReviewTitle" :
      kind === "club_duplicates" ? "clubsDuplicateTitle" : "reviewTitle"}`,
        bodyKey: `notificationCenter.${kind === "primary_club" ? "clubBody" :
          kind === "elo_visibility" ? "rankingBody" :
      kind === "profile_completion" ? "profileBody" :
      kind === "opponent_duplicates" ? "duplicatesBody" :
      kind === "club_review" ? "clubsReviewBody" :
      kind === "club_duplicates" ? "clubsDuplicateBody" : "reviewBody"}`,
        href, unread: isSuggestionUnread(state), dismissible: true,
      });
    };
    if (SMART_NOTIFICATION_REVIEW_ENABLED && completion.isSuccess && !isProfileComplete(completion.data) && !isSnoozed()) {
      suggest("suggestion:profile_completion", "profile_completion", "/app/settings#player-profile");
    }
    if (SMART_NOTIFICATION_REVIEW_ENABLED && identityCandidates.isSuccess) {
      const duplicates = possibleDuplicateOpponents(identityCandidates.data);
      if (duplicates.length) {
        const fingerprint = reviewFingerprint(
          duplicates.flatMap(pair => [pair.first.id, pair.second.id]),
        );
        suggest(`suggestion:opponent_duplicates:${fingerprint}`, "opponent_duplicates",
          "/app/matches#opponent-identity-review");
      }
    }
    if (CLUB_ELO_ENABLED && hostClubTasks.isSuccess) {
      const pending = clubHostTaskFingerprint(hostClubTasks.data);
      if (pending.length) suggest(`suggestion:club_review:${reviewFingerprint(pending)}`,
        "club_review", "/app/stats#historical-host-club-audit");
    }
    if (!CLUB_ELO_ENABLED && SMART_NOTIFICATION_REVIEW_ENABLED && historicalClubs.isSuccess) {
      const pending = matchesNeedingClubReview(historicalClubs.data);
      if (pending.length) {
        suggest(`suggestion:club_review:${reviewFingerprint(pending)}`,
          "club_review", "/app/stats#historical-host-club-audit");
      }
    }
    if (SMART_NOTIFICATION_REVIEW_ENABLED && clubs.isSuccess) {
      const duplicates = possibleDuplicateOwnClubs(clubs.data);
      if (duplicates.length) {
        suggest(`suggestion:club_duplicates:${reviewFingerprint(
          duplicates.flatMap(pair => [pair.first.club_id, pair.second.club_id]))}`,
          "club_duplicates", "/app/settings#my-tennis-clubs");
      }
    }
    if (shouldSuggestPrimaryClub(matchCount, clubs.isSuccess ? clubs.data : null)) {
      suggest("suggestion:primary_club", "primary_club", "/app/settings");
    }
    if (shouldSuggestEloVisibility(ranking.isSuccess ? ranking.data : null)) {
      suggest("suggestion:elo_visibility", "elo_visibility", "/app/rankings");
    }
    if (!reviewClicked && matches.isSuccess &&
        shouldSuggestPlayReview(matchCount, isAndroidInstalledApp(), readPlayReviewState(id))) {
      suggest("suggestion:play_review", "play_review", "external:google-play");
    }
    return visible;
  }, [
    id, locale, invitations.data, reports.data, states.data, states.isSuccess, states.isError,
    clubs.data, clubs.isSuccess, ranking.data, ranking.isSuccess,
    completion.data, completion.isSuccess, identityCandidates.data, identityCandidates.isSuccess,
    historicalClubs.data, historicalClubs.isSuccess, hostClubTasks.data, hostClubTasks.isSuccess,
    matchCount, matches.isSuccess, reviewClicked,
  ]);

  const markRead = async (key: string, dismiss = false) => {
    if (!key.startsWith(SUGGESTION_PREFIX)) return;
    if (dismiss && key === "suggestion:play_review" && id) {
      recordPlayReviewOptOut(id);
      setReviewClicked(true);
    }
    await changeState.mutateAsync({ key: key as NotificationSuggestionKey, dismiss });
  };

  const markReportOpened = (sharedMatchId: string) => {
    markReportRead.mutate(sharedMatchId);
  };

  const markPlayReviewOpened = () => {
    if (id) recordPlayReviewClick(id);
    setReviewClicked(true);
    void markRead("suggestion:play_review", true).catch(() => {
      // Local click state still prevents repeated prompting if SQL isn't deployed.
    });
  };

  return {
    items,
    unreadCount: items.filter((item) => item.unread).length,
    isLoading: invitations.isLoading || reports.isLoading || matches.isLoading ||
      (!!id && states.isLoading),
    markRead,
    markReportOpened,
    markPlayReviewOpened,
    saving: changeState.isPending,
  };
}

